/**
 * ContactManager
 *
 * Управление контактами: список, добавление, удаление.
 * Используется в CompatibilityPage для выбора партнёра.
 */

import React, { useState, useEffect } from 'react';
import api from '../api';
import PartnerForm from './PartnerForm';

// ============================================================
// Типы
// ============================================================

export interface Contact {
  id: string;
  name: string;
  relation: string;
  birth_date: string;
  birth_time: string | null;
  birth_place: string;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

interface ContactManagerProps {
  contacts: Contact[];
  selectedContactId: string | null;
  onSelectContact: (contact: Contact) => void;
  onDeleteContact: (contactId: string) => void;
  onAddContact: (contact: Contact) => void;
  onContactsLoaded: (contacts: Contact[]) => void;
}

// ============================================================
// Стили — Astranum design standard
// ============================================================

const cardStyle = (isMobile: boolean): React.CSSProperties => ({
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  padding: isMobile ? '16px' : '24px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
});

const btnPrimaryStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  border: 'none',
  padding: '12px 28px',
  borderRadius: '25px',
  color: '#0a0a1a',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const RELATION_LABELS: Record<string, string> = {
  partner: 'Романтические',
  family: 'Семейные',
  friend: 'Дружба',
  colleague: 'Деловые',
  other: 'Другое',
};

// ============================================================
// Компонент
// ============================================================

const ContactManager: React.FC<ContactManagerProps> = ({
  contacts,
  selectedContactId,
  onSelectContact,
  onDeleteContact,
  onAddContact,
  onContactsLoaded,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const res = await api.get<Contact[]>('/synastry/contacts');
      if (res.success && res.data) {
        onContactsLoaded(res.data);
      }
    } catch (err) {
      console.error('[ContactManager] Failed to load contacts:', err);
    }
    setLoading(false);
  };

  const handleFormSubmit = async (data: any) => {
    // Контакт уже создан через API в PartnerForm
    // Обновляем список
    await loadContacts();
    setShowForm(false);
  };

  const handleDelete = async (contactId: string, contactName: string) => {
    if (!window.confirm(`Удалить контакт "${contactName}"? Все расчёты совместимости с этим контактом будут потеряны.`)) {
      return;
    }
    try {
      const res = await api.delete(`/synastry/contacts/${contactId}`);
      if (res.success) {
        onDeleteContact(contactId);
      } else {
        alert(res.message || 'Не удалось удалить контакт');
      }
    } catch (err: any) {
      console.error('[ContactManager] Failed to delete contact:', err);
      alert(err?.response?.data?.message || 'Ошибка при удалении контакта');
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />

      {/* Заголовок + кнопка */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '12px' : '20px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            fontSize: isMobile ? '18px' : '22px',
            background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: 0,
          }}
        >
          Ваши контакты ({contacts.length})
        </h2>
        <button style={{ ...btnPrimaryStyle, fontSize: isMobile ? '13px' : '15px', padding: isMobile ? '10px 20px' : '12px 28px' }} onClick={() => setShowForm(true)}>
          + Добавить контакт
        </button>
      </div>

      {loading && (
        <p style={{ color: '#b0b0b0', textAlign: 'center', padding: isMobile ? '16px' : '24px', fontSize: isMobile ? '14px' : '16px' }}>
          Загрузка контактов...
        </p>
      )}

      {contacts.length === 0 && !loading && (
        <div style={{ ...cardStyle(isMobile), textAlign: 'center', padding: isMobile ? '32px 16px' : '48px 24px' }}>
          <div style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '16px' }}>💫</div>
          <p style={{ color: '#b0b0b0', fontSize: isMobile ? '14px' : '16px', marginBottom: '20px' }}>
            У вас пока нет контактов. Добавьте человека, чтобы рассчитать совместимость.
          </p>
          <button style={btnPrimaryStyle} onClick={() => setShowForm(true)}>
            Добавить первый контакт
          </button>
        </div>
      )}

      {/* Сетка контактов */}
      {contacts.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: isMobile ? '12px' : '16px',
          }}
        >
          {contacts.map((contact) => (
            <div
              key={contact.id}
              style={{
                ...cardStyle(isMobile),
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderColor:
                  selectedContactId === contact.id
                    ? 'rgba(212, 175, 55, 0.5)'
                    : 'rgba(212, 175, 55, 0.12)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor =
                  selectedContactId === contact.id
                    ? 'rgba(212, 175, 55, 0.5)'
                    : 'rgba(212, 175, 55, 0.12)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div
                onClick={() => onSelectContact(contact)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <div>
                  <h3
                    style={{
                      color: '#f0f0f0',
                      fontSize: isMobile ? '16px' : '18px',
                      margin: '0 0 8px',
                      fontFamily: 'var(--font-sans)',
                    }}
                  >
                    {contact.name}
                  </h3>
                  <p style={{ color: '#b0b0b0', fontSize: isMobile ? '12px' : '13px', margin: '0 0 4px' }}>
                    {new Date(contact.birth_date).toLocaleDateString('ru-RU')}
                  </p>
                  {contact.relation && (
                    <span
                      style={{
                        display: 'inline-block',
                        background: 'rgba(212, 175, 55, 0.15)',
                        color: '#d4af37',
                        fontSize: '11px',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        marginTop: '4px',
                      }}
                    >
                      {RELATION_LABELS[contact.relation] || contact.relation}
                    </span>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(contact.id, contact.name);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px 8px',
                    opacity: 0.6,
                  }}
                  title="Удалить"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Модалка добавления контакта */}
      {showForm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? '12px' : '20px',
          }}
          onClick={() => setShowForm(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <PartnerForm
              onSubmit={handleFormSubmit}
              onCancel={() => setShowForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManager;
